/**
 * 🌐 Timezone Utilities
 *
 * Provides timezone conversion and formatting functions for the EVOP Inspector.
 * Handles user timezone preferences and franchise-specific timezones.
 */

import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { format } from 'date-fns'

/**
 * Get the user's preferred timezone
 * Defaults to AEST (Australia/Brisbane - no DST) for consistency with server logs
 *
 * @returns IANA timezone identifier
 */
export const getUserTimezone = (): string => {
  // For admin views, always default to AEST (Brisbane - no DST)
  // This ensures consistent display matching server logs
  return 'Australia/Brisbane'
}

/**
 * Format a date/time in the user's preferred timezone
 * This is the primary function for displaying dates in admin views
 *
 * @param date - Date string or Date object to format
 * @param options - Formatting options
 * @returns Formatted date string in user's timezone
 */
export const formatInUserTZ = (
  date: string | Date | null | undefined,
  options?: {
    format?: string
    includeTimezone?: boolean
    fallback?: string
  }
): string => {
  const opts = {
    format: 'dd MMM yyyy, hh:mm a',
    includeTimezone: true,
    fallback: 'N/A',
    ...options
  }

  if (!date) {
    return opts.fallback
  }

  try {
    let dateObj: Date

    if (typeof date === 'string') {
      // Backend sends UTC timestamps without 'Z' suffix
      // We need to ensure they're interpreted as UTC
      let dateStr = date

      // Check if the string already has a timezone indicator
      const hasTimezone = dateStr.includes('Z') ||
                         dateStr.includes('+') ||
                         dateStr.includes('-') && dateStr.lastIndexOf('-') > 10

      if (!hasTimezone) {
        // No timezone indicator, assume it's UTC from backend
        dateStr = dateStr + 'Z'
      }

      dateObj = new Date(dateStr)
    } else {
      dateObj = date
    }

    if (isNaN(dateObj.getTime())) {
      return opts.fallback
    }

    const userTimezone = getUserTimezone()
    let formatted = formatInTimeZone(dateObj, userTimezone, opts.format)

    if (opts.includeTimezone) {
      const tzAbbr = getTimezoneAbbreviation(userTimezone, dateObj)
      formatted = `${formatted} ${tzAbbr}`
    }

    return formatted
  } catch (error) {
    console.error('Error formatting date in user timezone:', error)
    return opts.fallback
  }
}

/**
 * Format a date/time in a specific franchise partner's timezone
 * Used for displaying transaction dates in the FP's local time
 *
 * @param date - Date string or Date object to format
 * @param fpTimezone - IANA timezone identifier for the franchise
 * @param options - Formatting options
 * @returns Formatted date string in FP's timezone
 */
export const formatInFPTimezone = (
  date: string | Date | null | undefined,
  fpTimezone: string | null | undefined,
  options?: {
    format?: string
    includeTimezone?: boolean
    fallback?: string
  }
): string => {
  const opts = {
    format: 'dd MMM yyyy, hh:mm a',
    includeTimezone: false,
    fallback: 'N/A',
    ...options
  }

  if (!date) {
    return opts.fallback
  }

  // If no FP timezone specified, fall back to user timezone
  if (!fpTimezone) {
    return formatInUserTZ(date, options)
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (isNaN(dateObj.getTime())) {
      return opts.fallback
    }

    let formatted = formatInTimeZone(dateObj, fpTimezone, opts.format)

    if (opts.includeTimezone) {
      const tzAbbr = getTimezoneAbbreviation(fpTimezone, dateObj)
      formatted = `${formatted} ${tzAbbr}`
    }

    return formatted
  } catch (error) {
    console.error('Error formatting date in FP timezone:', error)
    // Fall back to user timezone if FP timezone is invalid
    return formatInUserTZ(date, options)
  }
}

/**
 * Get timezone abbreviation (e.g., AEST, AEDT, PST, EST)
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to check (for DST)
 * @returns Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone: string, date?: Date): string => {
  try {
    const targetDate = date || new Date()
    const formatted = formatInTimeZone(targetDate, timezone, 'zzz')

    // If we get GMT+XX format, try to use proper abbreviation
    if (formatted.startsWith('GMT')) {
      // Check if we're in daylight saving time for Australian timezones
      const month = targetDate.getMonth()
      const isDST = (timezone.includes('Sydney') || timezone.includes('Melbourne')) &&
                   (month >= 9 || month <= 3) // Oct-Mar is DST in Sydney/Melbourne

      const abbreviations: Record<string, string> = {
        'Australia/Sydney': isDST ? 'AEDT' : 'AEST',
        'Australia/Melbourne': isDST ? 'AEDT' : 'AEST',
        'Australia/Brisbane': 'AEST', // No DST
        'Australia/Perth': 'AWST',
        'Australia/Adelaide': isDST ? 'ACDT' : 'ACST',
        'Pacific/Auckland': isDST ? 'NZDT' : 'NZST',
        'America/New_York': 'EST',
        'America/Chicago': 'CST',
        'America/Denver': 'MST',
        'America/Los_Angeles': 'PST',
      }

      return abbreviations[timezone] || formatted
    }

    return formatted
  } catch {
    // Fallback to common abbreviations
    const abbreviations: Record<string, string> = {
      'Australia/Sydney': 'AEST',
      'Australia/Melbourne': 'AEST',
      'Australia/Brisbane': 'AEST',
      'Australia/Perth': 'AWST',
      'Australia/Adelaide': 'ACST',
      'Pacific/Auckland': 'NZST',
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
    }

    return abbreviations[timezone] || 'UTC'
  }
}

/**
 * Format with explicit timezone indicator
 * Shows the full timezone name for clarity
 *
 * @param date - Date to format
 * @param timezone - IANA timezone identifier
 * @returns Formatted string with full timezone
 */
export const formatWithTimezoneIndicator = (
  date: string | Date | null | undefined,
  timezone?: string
): string => {
  if (!date) return 'N/A'

  const tz = timezone || getUserTimezone()

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const formatted = formatInTimeZone(dateObj, tz, 'dd MMM yyyy, hh:mm a zzz')
    return `${formatted} (${tz})`
  } catch (error) {
    return 'Invalid date'
  }
}

/**
 * Convert a date from one timezone to another
 *
 * @param date - Source date
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns Date in target timezone
 */
export const convertTimezone = (
  date: Date,
  fromTimezone: string,
  toTimezone: string
): Date => {
  // Convert to UTC first
  const utcDate = zonedTimeToUtc(date, fromTimezone)
  // Then convert to target timezone
  return utcToZonedTime(utcDate, toTimezone)
}

/**
 * Get a list of common timezones for selection
 * Grouped by region for easier navigation
 */
export const getCommonTimezones = () => {
  return {
    'Australia': [
      { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
      { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
      { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
      { value: 'Australia/Perth', label: 'Perth (AWST)' },
      { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
      { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
      { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
    ],
    'New Zealand': [
      { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
      { value: 'Pacific/Chatham', label: 'Chatham Islands' },
    ],
    'United States': [
      { value: 'America/New_York', label: 'New York (EST/EDT)' },
      { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
      { value: 'America/Denver', label: 'Denver (MST/MDT)' },
      { value: 'America/Phoenix', label: 'Phoenix (MST)' },
      { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
      { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
    ],
  }
}

/**
 * Detect the most likely timezone for a franchise based on location
 *
 * @param country - Country code (AU, NZ, US)
 * @param state - State/province code
 * @returns Best guess timezone
 */
export const detectTimezoneFromLocation = (
  country: string,
  state?: string
): string => {
  const countryUpper = country?.toUpperCase()
  const stateUpper = state?.toUpperCase()

  if (countryUpper === 'AU') {
    const stateTimezones: Record<string, string> = {
      'NSW': 'Australia/Sydney',
      'VIC': 'Australia/Melbourne',
      'QLD': 'Australia/Brisbane',
      'WA': 'Australia/Perth',
      'SA': 'Australia/Adelaide',
      'TAS': 'Australia/Hobart',
      'NT': 'Australia/Darwin',
      'ACT': 'Australia/Sydney',
    }
    return stateTimezones[stateUpper || ''] || 'Australia/Sydney'
  }

  if (countryUpper === 'NZ') {
    return 'Pacific/Auckland'
  }

  if (countryUpper === 'US') {
    const stateTimezones: Record<string, string> = {
      // Eastern Time
      'NY': 'America/New_York',
      'FL': 'America/New_York',
      'GA': 'America/New_York',
      'NC': 'America/New_York',
      'SC': 'America/New_York',
      'VA': 'America/New_York',
      'DC': 'America/New_York',
      'MD': 'America/New_York',
      'PA': 'America/New_York',
      'NJ': 'America/New_York',
      'CT': 'America/New_York',
      'MA': 'America/New_York',
      'NH': 'America/New_York',
      'ME': 'America/New_York',
      'VT': 'America/New_York',
      'RI': 'America/New_York',

      // Central Time
      'TX': 'America/Chicago',
      'IL': 'America/Chicago',
      'WI': 'America/Chicago',
      'MN': 'America/Chicago',
      'IA': 'America/Chicago',
      'MO': 'America/Chicago',
      'AR': 'America/Chicago',
      'LA': 'America/Chicago',
      'MS': 'America/Chicago',
      'AL': 'America/Chicago',
      'TN': 'America/Chicago',
      'KY': 'America/Chicago',
      'IN': 'America/Chicago',
      'MI': 'America/Chicago',
      'OH': 'America/Chicago',
      'OK': 'America/Chicago',
      'KS': 'America/Chicago',
      'NE': 'America/Chicago',
      'SD': 'America/Chicago',
      'ND': 'America/Chicago',

      // Mountain Time
      'CO': 'America/Denver',
      'UT': 'America/Denver',
      'NM': 'America/Denver',
      'WY': 'America/Denver',
      'MT': 'America/Denver',
      'ID': 'America/Denver',
      'AZ': 'America/Phoenix', // Arizona doesn't observe DST

      // Pacific Time
      'CA': 'America/Los_Angeles',
      'WA': 'America/Los_Angeles',
      'OR': 'America/Los_Angeles',
      'NV': 'America/Los_Angeles',

      // Other
      'AK': 'America/Anchorage',
      'HI': 'Pacific/Honolulu',
    }
    return stateTimezones[stateUpper || ''] || 'America/New_York'
  }

  // Default to AEST for unknown locations
  return 'Australia/Sydney'
}

// Re-export commonly used functions for convenience
export { formatInTimeZone, utcToZonedTime, zonedTimeToUtc }